<template>
  <div class="manual-freight-panel">
    <div class="box-title">
      <h4>
        <i class="fal fa-hand-holding-usd"></i>
        ສິດປ້ອນຄ່າຂົນສົ່ງເອງ (Manual Freight)
      </h4>
    </div>
    <p class="manual-freight-hint">
      ຕາມຄ່າເລີ່ມຕົ້ນ <b>ບໍ່ມີໃຜ</b> ປ້ອນ ຫຼື ແກ້ໄຂຄ່າຂົນສົ່ງເອງໄດ້ (ລວມທັງການເງິນ, admin ແລະ ຜູ້ຈັດການສາຂາ) —
      ລະບົບຈະໃຊ້ຄ່າຂົນສົ່ງທີ່ຄິດໄລ່ເອງ ເວລາສ້າງບິນ ແລະ ຮັບບິນ pre-order, ແລະ ປະຕິເສດການແກ້ໄຂຄ່າຂົນສົ່ງໃນບິນ.
      ອະນຸຍາດ <b>ທັງສາຂາ</b> = ພະນັກງານທຸກຄົນທີ່ສັງກັດສາຂານີ້ປ້ອນໄດ້ (ທັງ Express ແລະ Logistics);
      ອະນຸຍາດ <b>ເມື່ອເປັນສາຂາປາຍທາງ</b> = ບິນທີ່ສົ່ງມາສາຂານີ້ ພະນັກງານທຸກຄົນ (ທຸກສາຂາ) ປ້ອນໄດ້
      (ສ້າງບິນ, ຮັບ pre-order ແລະ ແກ້ໄຂບິນ);
      ອະນຸຍາດ <b>ລາຍບຸກຄົນ</b> = ຜູ້ໃຊ້ຄົນນັ້ນປ້ອນໄດ້ ບໍ່ວ່າຈະຢູ່ສາຂາໃດ.
    </p>

    <div v-if="loadError" class="notification is-warning" :style="{ marginTop: '10px' }">
      <i class="fal fa-exclamation-triangle" :style="{ marginRight: '6px' }"></i>
      ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້ — ລະບົບຫຼັງບ້ານອາດຍັງບໍ່ຮອງຮັບ (migration ຍັງບໍ່ໄດ້ດຳເນີນ) ຫຼື ເກີດຂໍ້ຜິດພາດ.
      <vs-button transparent :style="{ display: 'inline-block', marginLeft: '8px' }" @click="fetchData()">
        ລອງໃໝ່
      </vs-button>
    </div>

    <template v-else-if="loaded">
      <div v-if="!info.enforced" class="notification is-info" :style="{ marginTop: '10px' }">
        <i class="fal fa-info-circle" :style="{ marginRight: '6px' }"></i>
        ການບັງຄັບໃຊ້ສິດນີ້ <b>ປິດຢູ່</b> (MANUAL_FREIGHT_PERMISSION_ENFORCED=false) — ຕອນນີ້ລະບົບໃຊ້ກົດເກົ່າ:
        Express ປ້ອນໄດ້ທຸກຄົນ, Logistics ສະເພາະສາຂາທີ່ເປີດໄວ້. ລາຍການຂ້າງລຸ່ມຈະມີຜົນເມື່ອເປີດການບັງຄັບໃຊ້.
      </div>

      <!-- 1. + 2. side by side: the two branch-level rows. -->
      <div class="columns manual-freight-section manual-freight-branch-columns">
      <!-- ══════════════════ 1. ທັງສາຂາ ══════════════════ -->
      <div class="column" :class="{ 'is-6': info.end_branch }">
        <h5 class="manual-freight-section-title">
          <i class="fal fa-store" :style="{ marginRight: '6px' }"></i>
          1. ອະນຸຍາດທັງສາຂາ
        </h5>
        <!-- One compact row: branch + status on the left, reason + action on the right. -->
        <div class="manual-freight-branch-row" :class="{ 'is-enabled': info.branch.allowed }">
          <div class="manual-freight-branch-info">
            <span class="manual-freight-branch-name">{{ info.branch.name || '-' }}</span>
            <span :class="info.branch.allowed ? 'tag-allowed' : 'tag-denied'">
              <i :class="info.branch.allowed ? 'fal fa-check-circle' : 'fal fa-times-circle'"
                 :style="{ marginRight: '4px' }"></i>
              {{ info.branch.allowed ? 'ອະນຸຍາດ — ພະນັກງານຂອງສາຂານີ້ປ້ອນໄດ້' : 'ບໍ່ອະນຸຍາດ' }}
            </span>
            <span v-if="info.branch.allowed && info.branch.note" class="manual-freight-note">
              · {{ info.branch.note }}
            </span>
          </div>
          <div class="manual-freight-branch-actions">
            <input v-if="!info.branch.allowed" class="input" v-model="branchNote"
                   placeholder="ເຫດຜົນ (ບໍ່ບັງຄັບ)" maxlength="255"/>
            <vs-button v-if="!info.branch.allowed" :loading="branchLoading && branchScope === 'branch'" :disabled="busy"
                       @click="openBranchModal('branch', 'allow')">
              <i class="fal fa-check-circle" :style="{ marginRight: '6px' }"></i>
              ອະນຸຍາດທັງສາຂາ
            </vs-button>
            <vs-button v-else danger flat :loading="branchLoading && branchScope === 'branch'" :disabled="busy"
                       @click="openBranchModal('branch', 'revoke')">
              <i class="fad fa-ban" :style="{ marginRight: '6px' }"></i>
              ຍົກເລີກສິດທັງສາຂາ
            </vs-button>
          </div>
        </div>
      </div>

      <!-- ══════════════════ 2. ເມື່ອເປັນສາຂາປາຍທາງ ══════════════════ -->
      <!-- Shown only when the backend reports the destination row (older backends do not). -->
      <div v-if="info.end_branch" class="column is-6">
        <h5 class="manual-freight-section-title">
          <i class="fal fa-map-marker-alt" :style="{ marginRight: '6px' }"></i>
          2. ອະນຸຍາດເມື່ອເປັນສາຂາປາຍທາງ
        </h5>
        <div class="manual-freight-branch-row" :class="{ 'is-enabled': info.end_branch.allowed }">
          <div class="manual-freight-branch-info">
            <span :class="info.end_branch.allowed ? 'tag-allowed' : 'tag-denied'">
              <i :class="info.end_branch.allowed ? 'fal fa-check-circle' : 'fal fa-times-circle'"
                 :style="{ marginRight: '4px' }"></i>
              {{ info.end_branch.allowed ? 'ອະນຸຍາດ — ບິນທີ່ສົ່ງມາສາຂານີ້ ທຸກຄົນປ້ອນໄດ້' : 'ບໍ່ອະນຸຍາດ' }}
            </span>
            <span v-if="info.end_branch.allowed && info.end_branch.note" class="manual-freight-note">
              · {{ info.end_branch.note }}
            </span>
          </div>
          <div class="manual-freight-branch-actions">
            <input v-if="!info.end_branch.allowed" class="input" v-model="endBranchNote"
                   placeholder="ເຫດຜົນ (ບໍ່ບັງຄັບ)" maxlength="255"/>
            <vs-button v-if="!info.end_branch.allowed" :loading="branchLoading && branchScope === 'end_branch'"
                       :disabled="busy" @click="openBranchModal('end_branch', 'allow')">
              <i class="fal fa-check-circle" :style="{ marginRight: '6px' }"></i>
              ອະນຸຍາດເມື່ອເປັນປາຍທາງ
            </vs-button>
            <vs-button v-else danger flat :loading="branchLoading && branchScope === 'end_branch'" :disabled="busy"
                       @click="openBranchModal('end_branch', 'revoke')">
              <i class="fad fa-ban" :style="{ marginRight: '6px' }"></i>
              ຍົກເລີກສິດປາຍທາງ
            </vs-button>
          </div>
        </div>
      </div>
      </div>

      <!-- ══════════════════ 3. ລາຍບຸກຄົນ ══════════════════ -->
      <div class="manual-freight-section">
        <h5 class="manual-freight-section-title">
          <i class="fal fa-users" :style="{ marginRight: '6px' }"></i>
          {{ info.end_branch ? '3.' : '2.' }} ອະນຸຍາດລາຍບຸກຄົນ (ຜູ້ໃຊ້ຂອງສາຂານີ້)
        </h5>
        <div class="columns" :style="{ marginTop: '6px', marginBottom: '0' }">
          <div class="column is-5">
            <input class="input" v-model="search" placeholder="ຄົ້ນຫາ ຊື່ ຫຼື ເບີໂທ"/>
          </div>
        </div>

        <div class="center">
          <vs-table>
            <template #thead>
              <vs-tr>
                <vs-th>ລຳດັບ</vs-th>
                <vs-th>ຊື່</vs-th>
                <vs-th>ເບີໂທ</vs-th>
                <vs-th>ສິດ (Role)</vs-th>
                <vs-th>ສະຖານະ</vs-th>
                <vs-th></vs-th>
              </vs-tr>
            </template>
            <template #tbody>
              <vs-tr :key="user.id" v-for="(user, index) in filteredUsers">
                <vs-td>{{ index + 1 }}</vs-td>
                <vs-td>{{ user.name || '-' }}</vs-td>
                <vs-td>{{ user.tel || '-' }}</vs-td>
                <vs-td>{{ (user.roles || []).join(', ') || '-' }}</vs-td>
                <vs-td>
                  <span :class="statusClass(user)">{{ statusText(user) }}</span>
                  <p v-if="user.note && user.allowed_self" class="manual-freight-note">{{ user.note }}</p>
                </vs-td>
                <vs-td>
                  <vs-button v-if="user.allowed_self" danger flat size="small" :disabled="busy"
                             :loading="userLoadingId === user.id" @click="revokeUser(user)">
                    ຍົກເລີກ
                  </vs-button>
                  <vs-button v-else flat size="small" :disabled="busy"
                             :loading="userLoadingId === user.id" @click="allowUser(user)">
                    ອະນຸຍາດ
                  </vs-button>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
          <p v-if="filteredUsers.length === 0" class="manual-freight-empty">
            {{ (info.users || []).length === 0 ? 'ສາຂານີ້ຍັງບໍ່ມີຜູ້ໃຊ້' : 'ບໍ່ພົບຜູ້ໃຊ້ທີ່ຄົ້ນຫາ' }}
          </p>
        </div>
      </div>
    </template>

    <Loading v-if="isLoading"/>

    <vs-dialog prevent-close v-model="branchModalActive">
      <div class="con-content" :style="{ marginTop: '15px' }">
        <p :style="{ fontSize: '18px', color: '#195BFF' }">{{ branchModalText }}</p>
      </div>
      <div class="con-footer" :style="{ marginTop: '25px' }">
        <vs-button :loading="branchLoading" transparent :style="{ color: 'crimson' }" @click="confirmBranch()">
          ຢືນຢັນ
        </vs-button>
        <vs-button @click="branchModalActive = false" dark transparent>ຍົກເລີກ</vs-button>
      </div>
    </vs-dialog>
  </div>
</template>
<script>
import Loading from '@/views/Loading';

/**
 * Branch edit page → ສິດປ້ອນຄ່າຂົນສົ່ງເອງ: put this branch (all its staff) or individual users of
 * this branch on the backend's manual-freight allowlist. SuperAdmin only (the API refuses others).
 */
export default {
  components: { Loading },

  data: () => ({
    isLoading: false,
    loaded: false,
    loadError: false,
    branchLoading: false,
    branchModalActive: false,
    branchAction: null, // 'allow' | 'revoke'
    branchScope: 'branch', // 'branch' (the branch's staff) | 'end_branch' (bills going to the branch)
    branchNote: '',
    endBranchNote: '',
    userLoadingId: null,
    /// One change at a time: every allow / revoke is followed by a reload, and two in flight
    /// could land their reloads out of order.
    mutating: false,
    fetchSeq: 0,
    search: '',
    info: {
      enforced: true,
      branch: { id: null, name: '', allowed: false, note: null },
      users: [],
    },
  }),

  computed: {
    // No action while a change or a reload is in flight — the rows on screen may be stale.
    busy() {
      return this.mutating || this.isLoading;
    },
    branchId() {
      return this.$route.params.id;
    },
    branchModalText() {
      if (this.branchScope === 'end_branch') {
        return this.branchAction === 'allow'
          ? 'ຢືນຢັນອະນຸຍາດ: ບິນທີ່ສົ່ງມາສາຂານີ້ ພະນັກງານທຸກຄົນ (ທຸກສາຂາ) ຈະປ້ອນ ແລະ ແກ້ໄຂຄ່າຂົນສົ່ງເອງໄດ້'
          : 'ຢືນຢັນຍົກເລີກ: ບິນທີ່ສົ່ງມາສາຂານີ້ຈະໃຊ້ຄ່າຂົນສົ່ງທີ່ລະບົບຄິດໄລ່ (ຍົກເວັ້ນຄົນ ຫຼື ສາຂາທີ່ມີສິດ)';
      }
      return this.branchAction === 'allow'
        ? 'ຢືນຢັນອະນຸຍາດ: ພະນັກງານທຸກຄົນຂອງສາຂານີ້ຈະປ້ອນ ແລະ ແກ້ໄຂຄ່າຂົນສົ່ງເອງໄດ້'
        : 'ຢືນຢັນຍົກເລີກ: ພະນັກງານຂອງສາຂານີ້ຈະປ້ອນຄ່າຂົນສົ່ງເອງບໍ່ໄດ້ (ຍົກເວັ້ນຄົນທີ່ອະນຸຍາດລາຍບຸກຄົນ)';
    },
    filteredUsers() {
      const term = (this.search || '').trim().toLowerCase();
      const users = this.info.users || [];
      if (!term) {
        return users;
      }
      return users.filter(user => (user.name || '').toLowerCase().includes(term)
          || String(user.tel || '').includes(term));
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    fetchData() {
      const seq = ++this.fetchSeq;
      this.isLoading = true;
      this.loadError = false;
      return this.$axios.get('manual-freight-permissions/branches/' + this.branchId).then(res => {
        if (seq !== this.fetchSeq) return; // a newer reload is on its way
        this.info = res.data.data;
        this.loaded = true;
        this.isLoading = false;
      }).catch(() => {
        if (seq !== this.fetchSeq) return;
        this.isLoading = false;
        this.loadError = true;
      });
    },
    // Mirrors the backend gate: the user's own row, or the row of their OWN branch (the first
    // branch they belong to — not necessarily this one).
    statusText(user) {
      const isOtherOwnBranch = user.own_branch_id !== null && user.own_branch_id !== this.info.branch.id;
      const otherName = user.own_branch_name || ('#' + user.own_branch_id);
      if (user.allowed_self) {
        return 'ອະນຸຍາດ (ລາຍບຸກຄົນ)';
      }
      if (user.covered_by_branch) {
        return isOtherOwnBranch ? 'ອະນຸຍາດ (ຜ່ານສາຂາຫຼັກ: ' + otherName + ')' : 'ອະນຸຍາດ (ຜ່ານສາຂານີ້)';
      }
      if (this.info.branch.allowed && isOtherOwnBranch) {
        return 'ບໍ່ອະນຸຍາດ — ສາຂາຫຼັກແມ່ນ ' + otherName;
      }
      return 'ບໍ່ອະນຸຍາດ';
    },
    statusClass(user) {
      return user.allowed ? 'tag-allowed' : 'tag-denied';
    },
    openBranchModal(scope, action) {
      this.branchScope = scope;
      this.branchAction = action;
      this.branchModalActive = true;
    },
    confirmBranch() {
      if (this.busy) return;
      const scope = this.branchScope;
      const noteKey = scope === 'end_branch' ? 'endBranchNote' : 'branchNote';
      const done = () => {
        this.branchLoading = false;
        this.branchModalActive = false;
      };
      this.branchLoading = true;
      if (this.branchAction === 'allow') {
        this.allow(scope, this.info.branch.id, this[noteKey] || null, ok => {
          done();
          if (ok) this[noteKey] = ''; // keep the typed reason when the save failed
        });
      } else {
        this.revoke(scope, this.info.branch.id, done);
      }
    },
    allowUser(user) {
      if (this.busy) return;
      this.userLoadingId = user.id;
      this.allow('user', user.id, null, () => {
        this.userLoadingId = null;
      });
    },
    revokeUser(user) {
      if (this.busy) return;
      this.userLoadingId = user.id;
      this.revoke('user', user.id, () => {
        this.userLoadingId = null;
      });
    },
    allow(type, id, note, done) {
      this.mutate(
        this.$axios.post('manual-freight-permissions', { subject_type: type, subject_id: id, note: note }),
        () => this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000),
        done
      );
    },
    revoke(type, id, done) {
      this.mutate(
        this.$axios.delete('manual-freight-permissions/' + type + '/' + id),
        () => this.$notification.OpenNotification_DeleteItem_OnSuccess('top-right', 'danger', 3000),
        done
      );
    },
    // Runs one change, then reloads; the buttons stay disabled until the reload has landed.
    // `done(ok)` learns whether the change itself succeeded.
    mutate(request, onSuccess, done) {
      this.mutating = true;
      let ok = false;
      request.then(() => {
        ok = true;
        onSuccess();
      }).catch(error => {
        this.notifyError(error);
      }).then(() => this.fetchData()).then(() => {
        done(ok);
        this.mutating = false;
      });
    },
    notifyError(error) {
      const status = error.response ? error.response.status : null;
      const code = error.response && error.response.data ? error.response.data.error : null;
      let text = null;
      if (status === 403) {
        text = 'ສະເພາະ SuperAdmin ເທົ່ານັ້ນທີ່ປ່ຽນສິດນີ້ໄດ້';
      } else if (status === 404 && code === 'NOT_FOUND') {
        text = 'ລາຍການນີ້ຖືກຍົກເລີກແລ້ວ — ໂຫຼດຂໍ້ມູນຄືນໃໝ່';
      } else if (code === 'SUBJECT_NOT_FOUND') {
        text = 'ບໍ່ພົບສາຂາ ຫຼື ຜູ້ໃຊ້ນີ້ໃນລະບົບ';
      } else if (status === 422) {
        text = 'ຂໍ້ມູນບໍ່ຖືກຕ້ອງ ກະລຸນາລອງໃໝ່';
      }
      if (text) {
        this.$notification.openNotification(
          { title: 'ສິດປ້ອນຄ່າຂົນສົ່ງເອງ', text: text },
          'top-right', 'danger', 5000
        );
      } else {
        this.$notification.OpenNotification_Error_Server('top-right', 'danger', 5000);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.manual-freight-panel {
  margin-top: 10px;

  .manual-freight-hint {
    color: #666;
    font-size: 14px;
    margin-top: 6px;
  }

  .manual-freight-section {
    margin-top: 22px;
    padding-top: 14px;
    border-top: 1px solid #e0e0e0;

    // The two branch-level rows as Bulma columns: the section owns the spacing, not the columns.
    &.manual-freight-branch-columns {
      margin-left: 0;
      margin-right: 0;
      margin-bottom: 0;

      > .column {
        padding: 0 12px 0 0;
      }

      // Stacked on a phone: keep the second row off the first.
      @media screen and (max-width: 768px) {
        > .column + .column {
          padding-top: 14px;
        }
      }
    }

    .manual-freight-section-title {
      font-size: 16px;
      font-weight: 600;
      color: #195BFF;
    }
  }

  // Compact branch row: info left, reason + action right; wraps to two lines on narrow screens.
  .manual-freight-branch-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 16px;
    margin-top: 8px;
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    text-align: left;

    &.is-enabled {
      border-color: #16a34a;
      background: #f6fef9;
    }

    .manual-freight-branch-info {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px 12px;
    }

    .manual-freight-branch-name {
      font-weight: 600;
      font-size: 15px;
    }

    .manual-freight-branch-actions {
      display: flex;
      align-items: center;
      gap: 8px;

      .input {
        width: 240px;
        height: 34px;
      }

      .vs-button {
        margin: 0;
        white-space: nowrap;
      }
    }
  }

  .manual-freight-note {
    margin-top: 4px;
    font-size: 12px;
    color: #888;
  }

  .manual-freight-empty {
    color: #999;
    font-size: 14px;
    padding: 12px 0;
    text-align: center;
  }

  .tag-allowed {
    color: #16a34a;
    font-weight: 600;
  }

  .tag-denied {
    color: #999;
  }

  // Lao inside Bulma inputs / Vuesax buttons otherwise falls back to the OS face.
  button, input {
    font-family: inherit;
  }
}
</style>
